import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { CrmBoardQuery, CrmDealDto } from '@upme/contracts';
import { Avatar, AvatarFallback, AvatarImage } from '@upme/ui/components/avatar';
import { Button } from '@upme/ui/components/button';
import { cn } from '@upme/ui/lib/utils';
import { crmContactDisplayName, deriveCrmCardRoles, instagramProfileUrl } from '@upme/view-logic';
import { ArrowLeft, ChevronLeft, ChevronRight, GitMerge, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../auth/auth-context';
import { BOT_STATUS_LABEL, botStatusOf, formatDateTime, formatUserName } from '../../users/lib/format';
import { userByIdOptions } from '../../users/queries';
import { CRM_PRODUCT_LABEL_RU, CRM_STAGE_LABEL_RU } from '../lib/stages';
import { crmPartyUnifiedPanelOptions, crmResolvePartyOptions } from '../queries';
import { CrmCommentsSection } from './crm-comments-section';
import { CrmContactContextSections } from './crm-contact-context-sections';
import { CrmManagerEventInterestsSection } from './crm-manager-event-interests-section';
import { CrmManyChatInquiriesSection } from './crm-manychat-inquiries-section';
import { CRM_PANEL_SURFACE } from './crm-panel-surface';
import { CrmPartyMergeDialog } from './crm-party-merge-dialog';
import { CrmRoleTag } from './crm-role-tag';

type Props = {
  deal: CrmDealDto;
  sequence: ReadonlyArray<CrmDealDto>;
  boardFilters: CrmBoardQuery;
  onClose: () => void;
  onSelectDeal: (deal: CrmDealDto) => void;
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function CrmUserDetailsPanel({ deal, sequence, boardFilters, onClose, onSelectDeal }: Props) {
  const canReadAccount = useAuth().adminRoles.includes('SUPERADMIN');
  const [mergeOpen, setMergeOpen] = useState(false);
  const isManyChat = deal.contact.inboundChannel === 'instagram_manychat';
  const isManual = deal.contact.inboundChannel === 'manual';
  const isExternalContact = isManyChat || isManual;
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    ...userByIdOptions(deal.contactUserId),
    enabled: canReadAccount && !isExternalContact,
  });
  const displayName = crmContactDisplayName(deal.contact);
  const roles = deriveCrmCardRoles(deal);
  const igUrl = instagramProfileUrl(deal.contact.instagramUsername ?? null);
  const manyChatContactId = deal.contact.manyChatContactId ?? deal.contactUserId;
  const manualLeadId = deal.contact.manualLeadId ?? deal.contactUserId;
  const index = sequence.findIndex((d) => d.id === deal.id);
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < sequence.length - 1;

  const resolveParty = useQuery({
    ...crmResolvePartyOptions({
      inboundChannel: deal.contact.inboundChannel,
      contactUserId: deal.contactUserId,
      ...(deal.contact.manyChatContactId ? { manyChatContactId: deal.contact.manyChatContactId } : {}),
    }),
  });
  const partyId = resolveParty.data?.partyId ?? null;
  const unified = useQuery({
    ...crmPartyUnifiedPanelOptions(partyId ?? ''),
    enabled: Boolean(partyId),
  });
  const mergedView = (unified.data?.identityCount ?? 0) > 1;

  function go(delta: -1 | 1) {
    const next = sequence[index + delta];
    if (next) onSelectDeal(next);
  }

  return (
    <aside
      className={cn(
        'flex flex-col bg-background',
        'max-lg:fixed max-lg:inset-0 max-lg:h-dvh max-lg:z-40 max-lg:border-0',
        'min-w-0',
        CRM_PANEL_SURFACE,
      )}
      aria-label="Карточка контакта"
    >
      <header className="flex shrink-0 items-start gap-2 border-b border-border/60 px-5 py-4">
        <Button type="button" variant="ghost" size="sm" className="mr-1 shrink-0 lg:hidden" onClick={onClose}>
          <ArrowLeft className="mr-1 size-5 stroke-[1.5]" aria-hidden />К доске
        </Button>
        <div className="flex min-w-0 flex-1 gap-3">
          <Avatar className="size-16 shrink-0 lg:size-[100px]">
            {deal.contact.photoUrl ? <AvatarImage src={deal.contact.photoUrl} alt="" /> : null}
            <AvatarFallback className="text-lg">{initials(displayName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-1 pt-1">
            <h2 className="text-xl font-medium leading-tight">{displayName}</h2>
            {isManual ? (
              <>
                <p className="text-lg text-muted-foreground">Лид · добавлен вручную</p>
                {deal.contact.contactPhone ? (
                  <p className="text-lg text-muted-foreground">{deal.contact.contactPhone}</p>
                ) : null}
                {deal.contact.username ? (
                  <a
                    href={`https://t.me/${deal.contact.username}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-lg text-muted-foreground underline-offset-2 hover:underline"
                  >
                    @{deal.contact.username}
                  </a>
                ) : null}
                {igUrl ? (
                  <a
                    href={igUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-lg text-muted-foreground underline-offset-2 hover:underline"
                  >
                    @{deal.contact.instagramUsername}
                  </a>
                ) : null}
                {deal.contact.leadNote ? (
                  <p className="text-base text-muted-foreground whitespace-pre-wrap">
                    {deal.contact.leadNote}
                  </p>
                ) : null}
              </>
            ) : isManyChat ? (
              <>
                <p className="text-lg text-muted-foreground">Instagram / ManyChat · без аккаунта UpMe</p>
                {igUrl ? (
                  <a
                    href={igUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-lg text-muted-foreground underline-offset-2 hover:underline"
                  >
                    @{deal.contact.instagramUsername}
                  </a>
                ) : (
                  <p className="text-lg text-muted-foreground">Instagram username не передан</p>
                )}
              </>
            ) : deal.contact.username ? (
              <a
                href={`https://t.me/${deal.contact.username}`}
                target="_blank"
                rel="noreferrer"
                className="text-lg text-muted-foreground underline-offset-2 hover:underline"
              >
                @{deal.contact.username}
              </a>
            ) : (
              <p className="text-lg text-muted-foreground">Telegram не указан</p>
            )}
            <div className="flex flex-wrap gap-1 pt-1">
              {roles.map((role) => (
                <CrmRoleTag key={role.kind} role={role} contact={deal.contact} />
              ))}
            </div>
          </div>
        </div>
        <div className="hidden shrink-0 flex-col gap-0.5 lg:flex">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="mb-1 size-12 rounded-full bg-muted/50"
            aria-label="Закрыть"
            onClick={onClose}
          >
            <X className="size-6 stroke-[1.75]" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10"
            aria-label="Предыдущий контакт"
            disabled={!hasPrev}
            onClick={() => go(-1)}
          >
            <ChevronLeft className="size-5 stroke-[1.5]" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10"
            aria-label="Следующий контакт"
            disabled={!hasNext}
            onClick={() => go(1)}
          >
            <ChevronRight className="size-5 stroke-[1.5]" />
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto gap-5 px-5 py-5 text-lg">
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={() => setMergeOpen(true)}
        >
          <GitMerge className="size-4" aria-hidden />
          Объединить с другим контактом
        </Button>
        <CrmPartyMergeDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          sourceDeal={deal}
          boardFilters={boardFilters}
        />

        {mergedView ? (
          <p className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-[15px] text-muted-foreground">
            Объединённая карточка: {unified.data?.identityCount} идентичностей, {unified.data?.deals.length}{' '}
            сделок, заметки и обращения со всех связанных контактов.
          </p>
        ) : null}

        {unified.data && unified.data.deals.length > 1 ? (
          <section className="space-y-2">
            <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">
              Все сделки
            </h3>
            <ul className="space-y-2">
              {unified.data.deals.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className="w-full rounded-md border border-border/60 px-3 py-2 text-left hover:bg-muted/40"
                    onClick={() => {
                      const found = sequence.find((x) => x.id === d.id);
                      if (found) onSelectDeal(found);
                    }}
                  >
                    <p className="font-medium leading-tight">{d.title}</p>
                    <p className="text-[15px] text-muted-foreground">
                      {CRM_STAGE_LABEL_RU[d.stage]} · {d.contactLabel}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="space-y-2">
          <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">
            {unified.data && unified.data.deals.length > 1 ? 'Текущая сделка' : 'Сделка'}
          </h3>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-muted-foreground">Этап</dt>
            <dd>{CRM_STAGE_LABEL_RU[deal.stage]}</dd>
            <dt className="text-muted-foreground">Продукт</dt>
            <dd>{CRM_PRODUCT_LABEL_RU[deal.productKind]}</dd>
            {deal.eventStartsAt ? (
              <>
                <dt className="text-muted-foreground">Событие</dt>
                <dd>
                  {deal.title} · {formatDateTime(deal.eventStartsAt)}
                </dd>
              </>
            ) : null}
            <dt className="text-muted-foreground">След. контакт</dt>
            <dd>{formatDateTime(deal.nextContactAt)}</dd>
          </dl>
        </section>

        {mergedView && unified.data ? (
          <>
            {unified.data.userIds.map((uid) => (
              <CrmManagerEventInterestsSection key={uid} contactUserId={uid} />
            ))}
            {unified.data.manyChatContactIds.map((cid) => (
              <CrmManyChatInquiriesSection key={cid} contactId={cid} />
            ))}
          </>
        ) : isManyChat ? (
          <CrmManyChatInquiriesSection contactId={manyChatContactId} />
        ) : isManual ? null : (
          <CrmManagerEventInterestsSection contactUserId={deal.contactUserId} />
        )}
        <CrmCommentsSection
          key={
            mergedView && partyId
              ? `party-${partyId}`
              : isManyChat
                ? `mc-${manyChatContactId}`
                : isManual
                  ? `ml-${manualLeadId}`
                  : deal.contactUserId
          }
          target={
            mergedView && partyId
              ? { partyId }
              : isManyChat
                ? { manyChatContactId: manyChatContactId }
                : isManual
                  ? { manualLeadId }
                  : { userId: deal.contactUserId }
          }
        />

        {canReadAccount &&
          !isExternalContact &&
          (isLoading ? (
            <p className="text-muted-foreground">Загрузка профиля…</p>
          ) : isError || !user ? (
            <p className="text-muted-foreground">Не удалось загрузить профиль.</p>
          ) : (
            <>
              <section className="space-y-2 border-t border-border/60 pt-4">
                <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">
                  Профиль
                </h3>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
                  <dt className="text-muted-foreground">Имя</dt>
                  <dd>{formatUserName(user)}</dd>
                  <dt className="text-muted-foreground">Город</dt>
                  <dd className="font-mono text-[15px]">{user.cityId ?? '—'}</dd>
                  <dt className="text-muted-foreground">Профиль</dt>
                  <dd>{deal.contact.profileCompletenessPercent}%</dd>
                  <dt className="text-muted-foreground">НТС</dt>
                  <dd>
                    {deal.contact.psychTestComplete
                      ? 'Тип заполнен (socionicsType)'
                      : 'Не пройден — ответы в Admin API не экспонируются'}
                  </dd>
                  <dt className="text-muted-foreground">Связи</dt>
                  <dd>{user.friendsCount}</dd>
                  <dt className="text-muted-foreground">Ивенты (посещено)</dt>
                  <dd>{user.meetingsCount}</dd>
                  <dt className="text-muted-foreground">Рефералы</dt>
                  <dd>{user.referralsCount}</dd>
                  <dt className="text-muted-foreground">Ивенты (орг., подтв.)</dt>
                  <dd>{deal.contact.organizedEventsWithVerifiedAttendance}</dd>
                  <dt className="text-muted-foreground">Бот</dt>
                  <dd>{BOT_STATUS_LABEL[botStatusOf(user)]}</dd>
                </dl>
              </section>

              <section className="space-y-2 border-t border-border/60 pt-4">
                <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">
                  Сообщения
                </h3>
                <p className="text-[15px] text-muted-foreground">
                  Внутренние сообщения из CRM пока не подключены — нет API доставки менеджер → пользователь
                  (см. docs/dev/crm-contact-card-handoff.md).
                </p>
              </section>

              <CrmContactContextSections contactUserId={deal.contactUserId} />

              <Link
                to="/users/$userId"
                params={{ userId: user.id }}
                className="inline-block text-lg text-muted-foreground underline-offset-2 hover:underline"
              >
                Полное управление аккаунтом →
              </Link>
            </>
          ))}
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-border/60 px-4 py-2 lg:hidden">
        <Button type="button" variant="outline" size="sm" disabled={!hasPrev} onClick={() => go(-1)}>
          Предыдущий
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={!hasNext} onClick={() => go(1)}>
          Следующий
        </Button>
      </footer>
    </aside>
  );
}
